"use client";

import React from "react";
import { Phone, Clock, ShieldCheck, Wrench, CircleCheck as CheckCircle2, ArrowRight, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const keyHighlights = [{
  id: 1,
  text: "Fast Dispatch Across Lovejoy"
}, {
  id: 2,
  text: "Licensed & Insured Technicians"
}, {
  id: 3,
  text: "Upfront, Transparent Pricing"
}, {
  id: 4,
  text: "100% Satisfaction Guaranteed"
}];
export default function Lovejoy() {
  return <section id="lovejoyhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Background Decorative Accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 right-0 h-[480px] w-[480px] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-[380px] w-[380px] rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Content Column */}
          <div className="flex flex-col items-start text-left lg:col-span-7">
            {/* Location & Status Badge */}
            <div className="mb-4 inline-flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 border-primary/20 bg-secondary/80 px-3 py-1 text-xs font-semibold text-secondary-foreground">
                <MapPin className="size-3.5 text-accent" />
                <span>Lovejoy, GA Service Area</span>
              </Badge>
              <Badge variant="default" className="gap-1.5 bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">
                <Clock className="size-3.5" />
                <span>24/7 Emergency Dispatch</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
              Plumbing Services in <span className="text-primary">Lovejoy, GA</span>
            </h1>

            {/* Subheading */}
            <p className="mt-4 text-lg font-medium text-foreground/90 sm:text-xl">
              Trusted Plumbing Help Available 24/7 in Lovejoy
            </p>

            {/* Description */}
            <p className="mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
              From burst pipes and clogged sewer drains to water heater failures, our local plumbers provide prompt, dependable repairs for Lovejoy homeowners and businesses day or night.
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-md transition-[background-color,box-shadow] hover:bg-accent/90">
                <Link to="/contact/" className="inline-flex items-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-md transition-[background-color,box-shadow] hover:bg-primary/90">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <Phone className="size-4 text-accent" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>

            {/* Trust Highlights Checklist */}
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {keyHighlights.map((highlight, index) => <div key={highlight.id} data-index={index} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 shrink-0 text-accent" />
                  <span>{highlight.text}</span>
                </div>)}
            </div>
          </div>

          {/* Hero Visual Column */}
          <div className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Main Image Container */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/lovejoy_plumbing_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/lovejoy_plumbing_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/lovejoy_plumbing_hero.png-wvc-sizes" alt="Professional plumber inspecting pipes in Lovejoy GA home" loading="eager" className="h-full min-h-[360px] w-full object-cover object-center sm:min-h-[420px]" />
                <div className="absolute inset-0 bg-linear-to-t from-primary/90 via-primary/20 to-transparent" />

                {/* Floating Status Card Inside Image */}
                <div className="absolute inset-x-4 bottom-4">
                  <Card className="border-border/40 bg-card/95 py-4 shadow-lg backdrop-blur-md">
                    <CardContent className="flex items-center gap-4 p-0 px-4">
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary text-accent">
                        <Wrench className="size-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Emergency Response
                        </p>
                        <p className="truncate text-sm font-bold text-card-foreground">
                          Plumbing Crews Active in Lovejoy
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Floating Review / Guarantee Badge */}
              <div className="absolute -top-4 -right-2 hidden rounded-xl border border-border bg-background p-3 shadow-lg sm:flex sm:items-center sm:gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-accent/20 text-accent">
                  <ShieldCheck className="size-6" />
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">100% Guaranteed Work</p>
                  <p className="text-xs text-muted-foreground">Licensed GA Contractors</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}